import { Alert, Box, Checkbox, FormControlLabel } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import { EVENT_TYPE_THERAPY_SESSION, EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import VcAuditBookedEventsView from './VcAuditBookedEventsView'

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

const BOOKED_EVENTS_VIEW_TABS = [
  { id: 'support-bookings', title: 'Support Bookings' },
  { id: 'vc-audit-sessions', title: 'VC Audit Sessions' },
]

function LegacyBookedEventsView({ showHeader = true }) {
  const [selectedEventType, setSelectedEventType] = useState(EVENT_TYPE_THERAPY_SESSION)
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(true)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  return (
    <>
      {showHeader ? <SupportHeader heading={'Bookings & Appointments'} /> : null}
      <div
        style={{
          background: '#35304a',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}
      >
        <div>
          <BaseUITabsComponent
            value={selectedEventType}
            onChange={setSelectedEventType}
            tabs={EVENT_TYPES_ARRAY}
            getTabLabel={(tab) => tab?.title}
            getTabId={(tab) => tab?.id}
          />
        </div>
        <div
          className="d-flex align-items-center justify-content-end gap-2"
          style={{ color: 'white !important' }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyPaidEvents}
                onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                sx={{
                  color: 'white !important',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            }
            label="Hide payment pending events"
            sx={{
              '& .MuiFormControlLabel-label': {
                color: 'white !important',
              },
            }}
          />
          <SortingHandler
            sortValue={sortingHandler}
            setSortValue={setSortingHandler}
            getOptionLabel={(option) => option?.label}
            getOptionValue={(option) => option?.value}
            sortOptions={SORT_OPTIONS}
            outerDivStyle={{
              marginLeft: '10px',
              color: 'white',
              fontSize: '14px',
              alignItems: 'center',
            }}
            buttonColor="primary"
            buttonVariant="contained"
          />
        </div>
      </div>
      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        <MentorPastEventsListing
          key={`${selectedEventType}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated"
          showMentorName
          hideActions
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          rightSideCustomComponent={({ event }) => (
            <SupportContactActions
              patientId={event?.patientId}
              practitionerId={event?.mentorId}
              bookedEventId={event?.id}
              clinicBrandId={event?.clinicBrandId}
              clinicId={event?.clinicId}
              targetRole="patient"
              eventType={event?.eventType || selectedEventType}
              title=""
              compact
              uiSurface="support-booked-events-strip"
              sx={{ minWidth: 240 }}
            />
          )}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}
    </>
  )
}

export default function BookedEvents() {
  const roleBooleans = getAllRolesInBoolean()
  const hasVcAuditAccess = roleBooleans?.isVcAuditAdmin || roleBooleans?.isVcAuditManager
  const hasL3TherapySupport = roleBooleans?.isL3TherapySupport
  const availableViews = useMemo(() => {
    const nextViews = []

    if (hasL3TherapySupport) {
      nextViews.push(BOOKED_EVENTS_VIEW_TABS[0])
    }

    if (hasVcAuditAccess) {
      nextViews.push(BOOKED_EVENTS_VIEW_TABS[1])
    }

    return nextViews
  }, [hasL3TherapySupport, hasVcAuditAccess])
  const [selectedViewId, setSelectedViewId] = useState(
    availableViews[0]?.id || null,
  )

  useEffect(() => {
    if (
      availableViews.length > 0 &&
      !availableViews.some((view) => view.id === selectedViewId)
    ) {
      setSelectedViewId(availableViews[0].id)
    }
  }, [availableViews, selectedViewId])

  if (!hasVcAuditAccess && !hasL3TherapySupport) {
    return <Navigate to="/support-home" replace />
  }

  const currentViewId = selectedViewId || availableViews[0]?.id
  const showRoleTabs = availableViews.length > 1

  return (
    <>
      <SupportHeader heading="Bookings & Appointments" />

      {showRoleTabs ? (
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 0 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            You have both support-booking and VC-audit access. Use the tabs
            below to switch between the full support bookings view and the
            VC-audit monitoring view.
          </Alert>
          <Box
            sx={{
              background: '#35304a',
              px: 1.5,
              pt: 1.25,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <BaseUITabsComponent
              value={currentViewId}
              onChange={setSelectedViewId}
              tabs={availableViews}
              getTabLabel={(tab) => tab?.title}
              getTabId={(tab) => tab?.id}
            />
          </Box>
        </Box>
      ) : null}

      {currentViewId === BOOKED_EVENTS_VIEW_TABS[1].id ? (
        <VcAuditBookedEventsView showHeader={false} />
      ) : (
        <LegacyBookedEventsView showHeader={false} />
      )}
    </>
  )
}
